import { Alert, AlertDescription, AlertIcon, AlertTitle, Box, Button, Flex, FlexProps, HStack, Image, Skeleton, Tag, TagLabel, Text, Tooltip, useColorModeValue, useDisclosure } from "@chakra-ui/react";
import { Globe2 } from "lucide-react";
import React, { useMemo, useState } from "react";
import { CLIENT_CONFIG } from "../core/client/buildConfig";
import type { ClientConfig } from "../core/client/ClientProvider";
import { useAgents } from "../hooks/agents/useAgents";
import { useHasNoAccessScopes } from "../hooks/auth/useHasNoAccessScopes";
import { useLanguage } from "../hooks/utility/useLanguage";
import { Banner } from "./Banner";
import { markdownToPlainText } from "../core/parsers/markdownParser";

type ChatHeaderProps = FlexProps & { isSidebarOpen?: boolean }

const ChatHeader: React.FC<ChatHeaderProps> = ({
  isSidebarOpen = true,
  ...props
}) => {
  const { currentAgent } = useAgents()
  const { t, currentLanguage } = useLanguage()
  const { hasNoAccessScopes } = useHasNoAccessScopes()

  const branding = (CLIENT_CONFIG as ClientConfig).theme.branding
  const customTexts = (CLIENT_CONFIG as ClientConfig).theme.customTexts
  const supportEmail = branding.supportEmail

  // Color mode values
  const headerBg = useColorModeValue('white', 'gray.800')
  const headerBorderColor = useColorModeValue('gray.100', 'gray.600')
  const headerTextColor = useColorModeValue('gray.800', 'white')
  const headerLogoUrl = useColorModeValue(branding.logoUrl, branding.logoDarkModeUrl ?? branding.logoUrl)
  const headerHoverColor = useColorModeValue('gray.600', 'gray.300')
  const modalBg = useColorModeValue('white', 'gray.800')

  const isInternetSearchEnabled = useMemo(() => {
    return Boolean(currentAgent?.tools?.includes('INTERNET_SEARCH'))
  }, [currentAgent?.tools])

  const noAccessMessage =
    customTexts.noAccessMessage?.[currentLanguage] ??
    customTexts.noAccessMessage?.en ??
    (supportEmail
      ? t('errors.noAccessScopesWithEmail', { email: supportEmail })
      : t('errors.noAccessScopes'))

  // The no-access banner is dismissible; it eats a lot of vertical space on
  // mobile. Dismissal is session-scoped (survives chat switches and reloads
  // within the tab, reappears on a fresh session) since the message is just an
  // informational prompt to contact support, not a one-time acknowledgement.
  const [isNoAccessBannerDismissed, setIsNoAccessBannerDismissed] = useState(
    () => sessionStorage.getItem('no_access_banner_dismissed') === 'true',
  )
  const dismissNoAccessBanner = () => {
    sessionStorage.setItem('no_access_banner_dismissed', 'true')
    setIsNoAccessBannerDismissed(true)
  }

  // Show a one-time modal/alert the first time an internet-search-enabled agent is active
  const { isOpen, onOpen, onClose } = useDisclosure()

  const acknowledgeWarning = () => {
    localStorage.setItem('internet_search_warning_ack', 'true')
    onClose()
  }

  return (
    <Box {...props} flex="0 0 auto" position="relative">
      <Flex
        as="header"
        h="60px"
        align="center"
        justify="center"
        bg={headerBg}
        px={6}
        py={3}
        position="relative"
        overflow="hidden"
        borderBottom="1px"
        borderColor={headerBorderColor}
      >
      {branding.showLogoInHeader && (
        <Image
          src={headerLogoUrl}
          alt={branding.companyName}
          position="absolute"
          // When the sidebar is collapsed a floating toggle button overlays the top-left
          // corner, so shift the logo right to clear it.
          left={isSidebarOpen ? 4 : "52px"}
          top="50%"
          transform="translateY(-50%)"
          h={branding.logoHeight ?? "52px"}
          objectFit="contain"
        />
      )}
      {!currentAgent ? (
        <Skeleton height="20px" width="140px" borderRadius="md" />
      ) : (
        <Tooltip
          label={currentAgent?.description ? markdownToPlainText(currentAgent.description) : undefined}
          hasArrow
          placement="bottom"
          isDisabled={!currentAgent?.description}
          bg="gray.700"
          color="white"
          fontSize="sm"
          maxW="300px"
          textAlign="center"
        >
          <Text
            fontSize="lg"
            fontWeight="semibold"
            fontFamily="body"
            color={headerTextColor}
            cursor={currentAgent?.description ? "help" : "default"}
            _hover={currentAgent?.description ? { color: headerHoverColor } : {}}
            transition="color 0.2s"
          >
            {currentAgent?.name ?? "Unknown Agent"}
          </Text>
        </Tooltip>
      )}

      {/* Right-side badges/actions */}
      {isInternetSearchEnabled && (
        <HStack position="absolute" right={4} top={3} spacing={2}>
          <Tooltip label={t('internetSearch.badgeTooltip')} hasArrow placement="bottom">
            <Tag size="md" variant="subtle" colorScheme="yellow" cursor="pointer" onClick={onOpen}>
              <Globe2 size={16} style={{ marginRight: 6 }} />
              <TagLabel>{t('internetSearch.badgeLabel')}</TagLabel>
            </Tag>
          </Tooltip>
        </HStack>
      )}
      </Flex>

      {isInternetSearchEnabled && <Banner message={t('internetSearch.inlineWarning')} />}

      {hasNoAccessScopes && !isNoAccessBannerDismissed && (
        <Banner message={noAccessMessage} onClose={dismissNoAccessBanner} closeLabel={t('common.close')} />
      )}

      {/* Modal-styled alert for the first-time acknowledgment */}
      {isInternetSearchEnabled && isOpen && (
        <Box position="fixed" inset={0} bg="blackAlpha.400" zIndex={1400} display="flex" alignItems="center" justifyContent="center">
          <Box bg={modalBg} borderRadius="lg" p={6} maxW="520px" w="90%" boxShadow="lg">
            <Alert status="warning" variant="left-accent" mb={4} alignItems="flex-start">
              <AlertIcon />
              <Box>
                <AlertTitle>{t('internetSearch.modalTitle')}</AlertTitle>
                <AlertDescription>{t('internetSearch.modalBody')}</AlertDescription>
              </Box>
            </Alert>
            <HStack justify="flex-end" spacing={3}>
              <Button onClick={acknowledgeWarning} colorScheme="yellow">
                {t('internetSearch.gotIt')}
              </Button>
            </HStack>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default React.memo(ChatHeader);
